import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "@/components/Reveal";
import { Phone, MessageSquare } from "lucide-react";

const FLEET_IMG = "https://images.pexels.com/photos/34902065/pexels-photo-34902065.jpeg";
const CONTAINERS_IMG = "https://images.pexels.com/photos/9694390/pexels-photo-9694390.jpeg";

const steps = [
  { n: "01", t: "Onboard in 24 hrs", d: "Send us your MC/DOT, W-9, COI and factoring info. We set you up on the boards under our authority." },
  { n: "02", t: "We source & negotiate", d: "Your dispatcher picks lanes to your target, then negotiates the rate before you see the rate confirmation." },
  { n: "03", t: "You drive, we handle", d: "Rate cons, BOLs, macro-points, detention claims — done in the background. Your job stays simple." },
  { n: "04", t: "Fast money", d: "POD to factor in minutes. Broker disputes handled by us. You keep rolling to the next load." },
];

export default function Fleet() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="fleet" ref={ref} className="bg-white py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="font-mono-label text-xs text-[#FF4500]">CHAPTER 04 / HOW IT WORKS</p>
              <h2 className="font-display font-bold text-4xl md:text-6xl mt-4 leading-[1]">
                From MC to<br/>money. <span className="italic">In four moves.</span>
              </h2>
            </div>
            <p className="max-w-md text-[#495057]">
              Our process is boring on purpose. Boring means predictable, and predictable means you can plan your month around it.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-black/10 border border-black/10">
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}
            className="md:col-span-7 bg-white relative h-[420px] md:h-[520px] overflow-hidden">
            <motion.img src={FLEET_IMG} alt="Semi-truck on a scenic highway" className="w-full h-[120%] object-cover" style={{ y: y1 }} />
            <div className="absolute bottom-0 left-0 right-0 p-10 bg-gradient-to-t from-black via-black/70 to-transparent text-white">
              <p className="font-mono-label text-[10px] text-[#FF4500]">DISPATCHING FOR</p>
              <h3 className="font-display font-semibold text-3xl md:text-4xl mt-2">Owner-operators &amp; small fleets</h3>
              <p className="text-sm text-white/80 mt-2 max-w-md">1–15 trucks. Dry van, reefer, flatbed, step deck, power only, hotshot. If your truck is legal, we&rsquo;ll dispatch it.</p>
            </div>
          </motion.div>
          <div className="md:col-span-5 bg-white p-8 md:p-10 flex flex-col justify-center gap-6">
            {steps.slice(0, 2).map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="pb-6 border-b border-black/10 last:border-b-0 last:pb-0">
                <p className="font-mono-label text-[10px] text-[#FF4500]">STEP {s.n}</p>
                <h4 className="font-display font-semibold text-2xl mt-2">{s.t}</h4>
                <p className="text-sm text-[#495057] mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>

          {/* Dark comms card */}
          <div className="md:col-span-5 bg-[#0A0A0A] text-white p-8 md:p-10 flex flex-col justify-between relative overflow-hidden grain">
            <div className="relative">
              <p className="font-mono-label text-[10px] text-[#FF4500]">ALWAYS ON</p>
              <h3 className="font-display font-semibold text-3xl mt-2">One number,<br/>every problem solved.</h3>
              <p className="text-sm text-white/70 mt-4 leading-relaxed max-w-sm">
                Detention at the shipper? Reefer alarm? Broker won&rsquo;t sign a TONU? We&rsquo;re the call — for you, the broker, and the shipper — until it&rsquo;s resolved.
              </p>
            </div>
            <div className="relative mt-8 grid grid-cols-2 gap-3">
              <a href="tel:+12145545884" className="sharp-btn inline-flex items-center justify-center gap-2 bg-[#FF4500] text-white px-4 py-3 text-sm font-medium hover:bg-white hover:text-black" data-testid="fleet-call">
                <Phone size={14}/> Call Dispatch
              </a>
              <a href="mailto:info@adtraxlogistics.com" className="sharp-btn inline-flex items-center justify-center gap-2 border border-white/40 text-white px-4 py-3 text-sm font-medium hover:bg-white hover:text-black" data-testid="fleet-email">
                <MessageSquare size={14}/> Email Us
              </a>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}
            className="md:col-span-7 bg-white relative h-[280px] md:h-[360px] overflow-hidden">
            <img src={CONTAINERS_IMG} alt="Freight yard with parked trailers" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/0 to-white/0" />
            <div className="absolute inset-0 flex items-center p-10">
              <div className="max-w-md">
                {steps.slice(2).map((s, i) => (
                  <motion.div
                    key={s.n}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.1 + i * 0.15 }}
                    className="mb-4 last:mb-0">
                    <p className="font-mono-label text-[10px] text-[#FF4500]">STEP {s.n}</p>
                    <h4 className="font-display font-semibold text-2xl mt-1">{s.t}</h4>
                    <p className="text-sm text-[#495057] mt-1 leading-relaxed">{s.d}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
